import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Loader2, Send, Sparkles, ArrowLeft, Scale, Factory, DollarSign, ShieldCheck, TrendingUp, Award } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { aiAdvisor } from "@/lib/ai";

const ADVISORS = {
  legal:         { icon: Scale,        label: "Legal Advisor",         color: "from-navy-700 to-navy-800",     blurb: "Entity choice, ToS, IP protection, tax registration, consumer-protection risk." },
  manufacturing: { icon: Factory,      label: "Manufacturing Advisor", color: "from-saffron-600 to-saffron-700", blurb: "MOQ, tooling, DVT/PVT/MP, freight modes, QA, packaging design." },
  pricing:       { icon: DollarSign,   label: "Pricing Advisor",       color: "from-forest-500 to-forest-700",   blurb: "Backer pricing, retail uplift, price anchoring, decoy tiers." },
  patent:        { icon: ShieldCheck,  label: "Patent Assistant",      color: "from-navy-600 to-navy-800",     blurb: "Provisional patents, design vs utility, disclosure risks, trademark basics." },
  investor:      { icon: TrendingUp,   label: "Investor Pitch",        color: "from-saffron-500 to-saffron-700", blurb: "10-slide decks, TAM/SAM/SOM, use-of-funds, follow-up templates." },
  grant:         { icon: Award,        label: "Grant Finder",          color: "from-forest-500 to-navy-700",     blurb: "Non-dilutive funding, government grants, accelerator tracks." },
};

const AdvisorPage = () => {
  const { kind } = useParams();
  const advisor = ADVISORS[kind];
  const { user, loading: authLoading, requireAuth } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scroller = useRef(null);

  useEffect(() => { if (!authLoading && !user) requireAuth("Sign in to talk to a Dwinov advisor."); }, [user, authLoading, requireAuth]);
  useEffect(() => { if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight; }, [messages, busy]);

  if (!advisor) return (
    <div className="mx-auto max-w-4xl px-4 py-16 text-center">
      <h1 className="font-display text-2xl font-bold text-navy-800">Advisor not found</h1>
      <Link to="/ai" className="mt-3 inline-flex items-center gap-1 text-saffron-700 font-semibold"><ArrowLeft className="h-4 w-4" /> Back to AI Tools</Link>
    </div>
  );

  const Icon = advisor.icon;

  const send = async () => {
    const msg = input.trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: msg }]);
    setBusy(true);
    try {
      const r = await aiAdvisor({ kind, message: msg });
      setMessages((prev) => [...prev, { role: "assistant", content: r.reply }]);
    } catch (e) { toast.error(e.message || "Failed"); } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-10" data-testid={`advisor-page-${kind}`}>
      <Link to="/ai" className="inline-flex items-center gap-1 text-navy-500 hover:text-saffron-700 text-sm font-semibold"><ArrowLeft className="h-3.5 w-3.5" /> All AI tools</Link>
      <div className="mt-3 flex items-start gap-4">
        <div className={`inline-grid place-items-center h-14 w-14 rounded-2xl bg-gradient-to-br ${advisor.color} text-white shadow-warm flex-shrink-0`}>
          <Icon className="h-6 w-6" />
        </div>
        <div>
          <div className="flex items-center gap-2 text-saffron-700"><Sparkles className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-[0.2em]">AI Advisor</span></div>
          <h1 className="mt-1 font-display text-3xl md:text-4xl font-bold tracking-tight text-navy-800">{advisor.label}</h1>
          <p className="mt-2 text-navy-600 max-w-2xl">{advisor.blurb}</p>
        </div>
      </div>

      <div className="mt-8 rounded-2xl border border-warm-200 bg-white flex flex-col overflow-hidden" style={{ minHeight: "60vh" }}>
        <div ref={scroller} className="flex-1 overflow-y-auto p-5 space-y-4" style={{ maxHeight: "60vh" }}>
          {messages.length === 0 && (
            <div className="text-center text-sm text-navy-500 py-8">Ask anything. Answers include markdown headings, tables, and bullet lists where helpful.</div>
          )}
          {messages.map((m, i) => (
            <div key={i} data-testid={`advisor-msg-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-[15px] leading-[1.7] ${m.role === "user" ? "bg-navy-800 text-white" : "bg-cream-100 border border-warm-200 text-navy-800"}`}>
                {m.role === "assistant"
                  ? <div className="prose prose-sm max-w-none prose-headings:font-display prose-headings:text-navy-800 prose-headings:tracking-tight prose-p:my-2"><ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown></div>
                  : <span className="whitespace-pre-wrap">{m.content}</span>
                }
              </div>
            </div>
          ))}
          {busy && (<div className="flex gap-1.5"><span className="h-2 w-2 rounded-full bg-saffron-500 animate-bounce" /><span className="h-2 w-2 rounded-full bg-saffron-500 animate-bounce" style={{ animationDelay: "150ms" }} /><span className="h-2 w-2 rounded-full bg-saffron-500 animate-bounce" style={{ animationDelay: "300ms" }} /></div>)}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-warm-200 bg-cream-100/60 p-4 flex items-center gap-2">
          <input value={input} onChange={(e) => setInput(e.target.value)} placeholder={`Ask the ${advisor.label}…`} data-testid="advisor-input" disabled={busy} className="flex-1 h-11 rounded-2xl border border-warm-200 bg-white px-4 text-sm focus:outline-none focus:border-saffron-500 focus:ring-4 focus:ring-saffron-500/15 transition" />
          <button type="submit" disabled={busy || !input.trim()} data-testid="advisor-send" className="btn-saffron grid place-items-center h-11 w-11 rounded-2xl disabled:opacity-50" aria-label="Send"><Send className="h-4 w-4" /></button>
        </form>
      </div>
    </div>
  );
};

export default AdvisorPage;
export { ADVISORS };
